﻿class BreedSelector {
  constructor(inputElement, breeds = [], formElement = null) {
    this.input = inputElement;
    this.formElement = formElement;
    this.breeds = breeds.filter(b => b && b.trim()).sort();
    this.selectedBreed = null;
    this.listbox = null;
    this.init();
  }

  getSpeciesFromForm() {
    if (!this.formElement) return null;
    
    const productHandle = this.formElement.getAttribute('data-product-handle');
    const productId = this.formElement.getAttribute('data-product-id');
    
    // Extract species from product handle (e.g., WR-CAN, WR-AVIAN)
    if (productHandle && productHandle.startsWith('WR-')) {
      return productHandle.split('-')[1];
    }
    
    return null;
  }

  init() {
    this.input.setAttribute('autocomplete', 'off');
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-haspopup', 'listbox');
    this.createListbox();
    this.attachEventListeners();
  }

  createListbox() {
    const wrapper = document.createElement('div');
    wrapper.className = 'breed-selector-wrapper';
    wrapper.style.position = 'relative';
    wrapper.style.display = 'inline-block';
    wrapper.style.width = '100%';

    this.listbox = document.createElement('ul');
    this.listbox.className = 'breed-listbox';
    this.listbox.setAttribute('role', 'listbox');
    this.listbox.style.display = 'none';
    this.listbox.style.position = 'absolute';
    this.listbox.style.top = '100%';
    this.listbox.style.left = '0';
    this.listbox.style.right = '0';
    this.listbox.style.maxHeight = '250px';
    this.listbox.style.overflowY = 'auto';
    this.listbox.style.backgroundColor = '#fff';
    this.listbox.style.border = '1px solid #ddd';
    this.listbox.style.borderTop = 'none';
    this.listbox.style.margin = '0';
    this.listbox.style.padding = '0';
    this.listbox.style.listStyle = 'none';
    this.listbox.style.zIndex = '1000';
    this.listbox.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';

    this.input.parentNode.insertBefore(wrapper, this.input);
    wrapper.appendChild(this.input);
    wrapper.appendChild(this.listbox);
  }

  attachEventListeners() {
    this.input.addEventListener('input', (e) => this.handleInput(e));
    this.input.addEventListener('focus', (e) => this.handleFocus(e));
    this.input.addEventListener('blur', (e) => this.handleBlur(e));
    this.input.addEventListener('keydown', (e) => this.handleKeydown(e));
    document.addEventListener('click', (e) => this.handleClickOutside(e));
  }

  handleInput(e) {
    const query = e.target.value.toLowerCase().trim();
    this.selectedBreed = null;
    this.showMatches(query);
  }

  handleFocus(e) {
    const query = e.target.value.toLowerCase().trim();
    this.showMatches(query || '');
  }

  handleBlur(e) {
    setTimeout(() => {
      const value = e.target.value.trim();
      if (value && !this.breeds.some(b => b.toLowerCase() === value.toLowerCase())) {
        this.input.classList.add('breed-invalid');
        this.input.setAttribute('aria-invalid', 'true');
      } else if (value) {
        this.input.classList.remove('breed-invalid');
        this.input.setAttribute('aria-invalid', 'false');
        const matched = this.breeds.find(b => b.toLowerCase() === value.toLowerCase());
        this.input.value = matched;
      }
      this.listbox.style.display = 'none';
    }, 200);
  }

  handleKeydown(e) {
    const items = Array.from(this.listbox.querySelectorAll('[role="option"]'));
    if (!items.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.focusNext(items);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.focusPrev(items);
        break;
      case 'Enter':
        e.preventDefault();
        const focused = this.listbox.querySelector('[role="option"][aria-selected="true"]');
        if (focused) this.selectBreed(focused.textContent);
        break;
      case 'Escape':
        e.preventDefault();
        this.listbox.style.display = 'none';
        break;
    }
  }

  focusNext(items) {
    const current = this.listbox.querySelector('[role="option"][aria-selected="true"]');
    const idx = items.indexOf(current);
    const next = items[idx + 1] || items[0];
    this.setFocused(next);
  }

  focusPrev(items) {
    const current = this.listbox.querySelector('[role="option"][aria-selected="true"]');
    const idx = items.indexOf(current);
    const prev = items[idx - 1] || items[items.length - 1];
    this.setFocused(prev);
  }

  setFocused(item) {
    this.listbox.querySelectorAll('[role="option"]').forEach(el => {
      el.setAttribute('aria-selected', 'false');
      el.style.backgroundColor = '';
    });
    item.setAttribute('aria-selected', 'true');
    item.style.backgroundColor = '#f0f0f0';
    item.scrollIntoView({ block: 'nearest' });
  }

  showMatches(query) {
    this.listbox.innerHTML = '';
    const matches = this.breeds.filter(breed => 
      breed.toLowerCase().includes(query)
    ).slice(0, 50);

    if (!matches.length) {
      this.listbox.style.display = 'none';
      return;
    }

    matches.forEach((breed, idx) => {
      const li = document.createElement('li');
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', idx === 0 ? 'true' : 'false');
      li.className = 'breed-option';
      li.textContent = breed;
      li.style.cssText = 'padding: 8px 12px; cursor: pointer; border-bottom: 1px solid #eee;';
      if (idx === 0) li.style.backgroundColor = '#f0f0f0';
      li.addEventListener('click', () => this.selectBreed(breed));
      li.addEventListener('mouseover', () => this.setFocused(li));
      this.listbox.appendChild(li);
    });

    this.listbox.style.display = 'block';
  }

  selectBreed(breed) {
    this.selectedBreed = breed;
    this.input.value = breed;
    this.input.classList.remove('breed-invalid');
    this.input.setAttribute('aria-invalid', 'false');
    this.listbox.style.display = 'none';
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleClickOutside(e) {
    if (!e.target.closest('.breed-selector-wrapper')) {
      this.listbox.style.display = 'none';
    }
  }

  validate() {
    const value = this.input.value.trim();
    if (!value) return true;
    const isValid = this.breeds.some(b => b.toLowerCase() === value.toLowerCase());
    if (!isValid) {
      this.input.classList.add('breed-invalid');
      this.input.setAttribute('aria-invalid', 'true');
    }
    return isValid;
  }
}

function initializeBreedSelector() {
  const breedInput = document.querySelector('input[name="properties[Breed]"]');
  if (!breedInput || breedInput._breedSelector) return;
  
  const form = breedInput.closest('form') || breedInput.closest('product-form');
  const variantIdInput = form?.querySelector('[name="id"]');
  const variantId = variantIdInput?.value;
  
  console.log('=== BreedSelector Init ===');
  console.log('window.SHOPIFY_BREED_LIST_RAW:', window.SHOPIFY_BREED_LIST_RAW);
  console.log('Type:', typeof window.SHOPIFY_BREED_LIST_RAW);
  console.log('Is array?', Array.isArray(window.SHOPIFY_BREED_LIST_RAW));
  console.log('Length:', window.SHOPIFY_BREED_LIST_RAW?.length);
  
  if (!variantId) {
    console.log('No variant ID found');
    return;
  }
  
  // Fetch product data and initialize breed selector
  fetch(window.location.pathname + '.json')
    .then(r => r.json())
    .then(data => {
      console.log('Product data fetched');
      
      if (!data.product?.variants) {
        console.log('No variants in product data');
        return;
      }
      
      // Find the current variant's SKU
      const variant = data.product.variants.find(v => v.id.toString() === variantId);
      if (!variant) {
        console.log('Variant not found');
        return;
      }
      
      const workRequestCode = variant.sku?.toUpperCase();
      console.log('Detected work request code (SKU):', workRequestCode);
      
      // Check if this work request type has breed selector enabled
      const enabledWorkRequests = window.SHOPIFY_BREED_SELECTION_ENABLED || new Set();
      console.log('Enabled work requests:', Array.from(enabledWorkRequests));
      console.log('Is enabled?', enabledWorkRequests.has(workRequestCode));
      
      if (!enabledWorkRequests.has(workRequestCode)) {
        console.log('Not in enabled list, skipping breed selector');
        return;
      }
      
      // Use breed array from metafield
      let breeds = [];
      if (Array.isArray(window.SHOPIFY_BREED_LIST_RAW)) {
        console.log('Processing breed array...');
        breeds = window.SHOPIFY_BREED_LIST_RAW
          .map(b => (b && typeof b === 'string' ? b.trim() : ''))
          .filter(b => b && b.length > 0);
      } else {
        console.log('SHOPIFY_BREED_LIST_RAW is not an array!');
      }
      
      console.log('Breed count:', breeds.length);
      console.log('First 5 breeds:', breeds.slice(0, 5));
      
      if (breeds.length > 0) {
        breedInput._breedSelector = new BreedSelector(breedInput, breeds, form);
        console.log('BreedSelector initialized successfully');
      } else {
        console.warn('BreedSelector: No breed data available');
      }
    })
    .catch(e => console.error('Failed to fetch product data:', e));
}

// Try on DOMContentLoaded
document.addEventListener('DOMContentLoaded', initializeBreedSelector);

// Also try after a delay (for dynamically rendered forms)
setTimeout(initializeBreedSelector, 500);
setTimeout(initializeBreedSelector, 1500);
setTimeout(initializeBreedSelector, 3000);

// Watch for dynamically added inputs
const observer = new MutationObserver(() => {
  const breedInput = document.querySelector('input[name="properties[Breed]"]');
  if (breedInput && !breedInput._breedSelector) {
    initializeBreedSelector();
  }
});

observer.observe(document.body, { childList: true, subtree: true });
